import React, { useEffect, useRef } from "react";
import { Button, Flex, Text, Link, LoadingIndicator } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { useChatMessages } from "../../hooks/useChatMessages";
import { useMessageFeedback } from "../../hooks/useMessageFeedback";
import { findLastAssistantMessageIndex } from "../../utils";
import { useAnalyticsAI } from "../AnalyticsAI/context";
import { ChatInput } from "../ChatInput";
import { LookerEmbed } from "../LookerEmbed";
import { Message } from "../Message";
import * as styles from "./styles_vanilla.css";

export const ChatInterface: React.FC = () => {
  const { isChatCollapsed, toggleChatCollapse, conversationContext } = useAnalyticsAI();

  const {
    messages,
    handleSubmit,
    isLoading,
    listMessagesLoading,
    updateMessageFeedback,
    lastValidUrl,
    isRefreshingUrl,
  } = useChatMessages({
    conversationId: conversationContext.conversationId,
    exploreId: conversationContext.exploreId,
    modelName: conversationContext.modelName,
  });

  const { updateFeedback } = useMessageFeedback({
    onFeedbackUpdate: updateMessageFeedback,
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const hasSubmittedInitialPromptRef = useRef(false);
  const lastAssistantIndex = findLastAssistantMessageIndex(messages);

  useEffect(() => {
    if (
      conversationContext.initialPrompt &&
      !hasSubmittedInitialPromptRef.current &&
      !isLoading
    ) {
      hasSubmittedInitialPromptRef.current = true;
      void handleSubmit(conversationContext.initialPrompt);
    }
  }, [conversationContext.initialPrompt, isLoading, handleSubmit]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <Flex flexGrow={1} className={styles.chatContentContainer}>
      {!isChatCollapsed && (
        <Flex flexDirection="column" className={styles.chatContainer}>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            className={styles.chatHeaderSection}
          >
            <Text variant="secondary-md" className={styles.chatHeaderTitle}>
              CHATTING...
            </Text>
            <Button
              icon="arrow-left-to-line"
              onClick={toggleChatCollapse}
              data-testid="chat-collapse-button"
              variant="neutral"
            />
          </Flex>
          <div ref={scrollRef} className={styles.chatMessagesContainer}>
            {listMessagesLoading && (
              <Flex
                alignItems="center"
                justifyContent="center"
                className={styles.chatEmptyState}
              >
                <LoadingIndicator />
              </Flex>
            )}
            {!listMessagesLoading && (
              <>
                {messages.length > 0 ? (
                  <Flex flexDirection="column" gap={Sizes.LG}>
                    {messages.map((message, index) => (
                      <Message
                        key={message.id}
                        message={message}
                        isChatInProgress={isLoading}
                        isLastAssistantMessage={index === lastAssistantIndex}
                        onFollowupClick={(question: string) => void handleSubmit(question)}
                        onFeedbackClick={(feedback) =>
                          void updateFeedback(message.id, feedback)
                        }
                      />
                    ))}
                  </Flex>
                ) : (
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    className={styles.chatEmptyState}
                  >
                    <Text variant="secondary-md">Start a conversation</Text>
                  </Flex>
                )}
              </>
            )}
          </div>
          <Flex gap={Sizes.SM} flexDirection="column" className={styles.chatInputSection}>
            <ChatInput
              placeholder="Ask about activity"
              onSubmit={(prompt) => void handleSubmit(prompt)}
              disabled={isLoading}
            />
            <Text variant="secondary-sm">
              Responses are AI-generated. Please verify important details. Only select data
              sources are currently accessible.{" "}
              <Link to="#" isInline variant="muted" className={styles.chatDisclaimerLink}>
                Learn more
              </Link>
            </Text>
          </Flex>
        </Flex>
      )}
      <LookerEmbed url={isRefreshingUrl ? undefined : lastValidUrl} />
    </Flex>
  );
};
